import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { DEFAULT_FITNESS_INSTRUCTOR_ID, isFitnessInstructorId, type FitnessInstructorId } from '@/lib/fitnessInstructor';
import { getExplicitFitnessMovement } from '@/lib/fitnessExerciseIdentity';
import { fitnessExercisePresentationName } from '@/lib/fitnessExercisePresentation';

export const FITNESS_EXERCISE_NOTE_LIMIT = 2000;
type PreferenceRow = { user_id: string; instructor_id: FitnessInstructorId; updated_at: string };
type NoteRow = { user_id: string; exercise_key: string; notes: string; updated_at: string };
type PreferenceTable<Row> = { Row: Row; Insert: Omit<Row, 'updated_at'> & { updated_at?: string }; Update: Partial<Row>; Relationships: [] };
type PreferenceDatabase = Database & { public: { Tables: Database['public']['Tables'] & {
  fitness_user_preferences: PreferenceTable<PreferenceRow>;
  fitness_exercise_notes: PreferenceTable<NoteRow>;
} } };
// The additive migration ships with this narrowly typed extension. The generated
// client type remains untouched until its next complete schema regeneration.
const client = supabase as unknown as SupabaseClient<PreferenceDatabase>;

export function fitnessExerciseNoteKey(name: string, muscle: string): string {
  const presentation = fitnessExercisePresentationName(name, muscle);
  const movement = getExplicitFitnessMovement(presentation);
  const key = movement ? 'movement:' + movement.id : 'source:' + JSON.stringify([presentation, muscle]);
  if (!name.trim() || key.length > 600 || key.includes('\u0000')) throw new Error('Invalid exercise identity');
  return key;
}
export function validateFitnessExerciseNote(notes: unknown): asserts notes is string {
  if (typeof notes !== 'string' || notes.length > FITNESS_EXERCISE_NOTE_LIMIT || notes.includes('\u0000')) {
    throw new Error('Invalid exercise note');
  }
}
async function assertOwner(ownerId: string, assertCurrent: () => void) {
  assertCurrent();
  const auth = await supabase.auth.getUser();
  assertCurrent();
  if (auth.error || auth.data.user?.id !== ownerId) throw new Error('Fitness account changed');
}
export async function readFitnessPreferences(ownerId: string, assertCurrent: () => void) {
  const { data, error } = await client.from('fitness_user_preferences').select('user_id,instructor_id')
    .eq('user_id', ownerId).maybeSingle();
  assertCurrent();
  if (error) throw new Error('Fitness preferences unavailable');
  if (data && (data.user_id !== ownerId || !isFitnessInstructorId(data.instructor_id))) throw new Error('Invalid fitness preferences');
  return { instructorId: data?.instructor_id ?? DEFAULT_FITNESS_INSTRUCTOR_ID };
}
export async function saveFitnessInstructorPreference(ownerId: string, instructorId: FitnessInstructorId, assertCurrent: () => void) {
  if (!isFitnessInstructorId(instructorId)) throw new Error('Invalid instructor');
  await assertOwner(ownerId, assertCurrent);
  const { data, error } = await client.from('fitness_user_preferences')
    .upsert({ user_id: ownerId, instructor_id: instructorId }, { onConflict: 'user_id' })
    .select('user_id,instructor_id');
  assertCurrent();
  if (error || data?.length !== 1 || data[0].user_id !== ownerId || data[0].instructor_id !== instructorId) {
    throw new Error('Instructor preference not confirmed');
  }
  return instructorId;
}
export async function readFitnessExerciseNote(ownerId: string, exerciseKey: string, assertCurrent: () => void) {
  const { data, error } = await client.from('fitness_exercise_notes').select('user_id,exercise_key,notes')
    .eq('user_id', ownerId).eq('exercise_key', exerciseKey).maybeSingle();
  assertCurrent();
  if (error) throw new Error('Exercise notes unavailable');
  if (data) {
    if (data.user_id !== ownerId || data.exercise_key !== exerciseKey) throw new Error('Invalid exercise notes');
    validateFitnessExerciseNote(data.notes);
  }
  return { exerciseKey, notes: data?.notes ?? '' };
}
export async function saveFitnessExerciseNote(ownerId: string, exerciseKey: string, notes: string, assertCurrent: () => void) {
  validateFitnessExerciseNote(notes);
  if (!exerciseKey || exerciseKey.length > 600 || exerciseKey.includes('\u0000')) throw new Error('Invalid exercise identity');
  await assertOwner(ownerId, assertCurrent);
  const { data, error } = await client.from('fitness_exercise_notes')
    .upsert({ user_id: ownerId, exercise_key: exerciseKey, notes }, { onConflict: 'user_id,exercise_key' })
    .select('user_id,exercise_key,notes');
  assertCurrent();
  if (error || data?.length !== 1 || data[0].user_id !== ownerId || data[0].exercise_key !== exerciseKey || data[0].notes !== notes) {
    throw new Error('Exercise note not confirmed');
  }
  return notes;
}
